// frontend/src/pages/UboCreate.tsx
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiUboCreate } from "../lib/api";

type Country = { code: string; name: string };
const incomeOptions = [
  ["salary", "Salary / employment"],
  ["business_income", "Business income"],
  ["investment_income", "Investment income"],
  ["savings", "Savings"],
  ["pension", "Pension"],
  ["inheritance", "Inheritance"],
  ["property_sale", "Property sale"],
  ["other", "Other"],
];

export default function UboCreate() {
  const nav = useNavigate();
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [isPep, setIsPep] = useState("");
  const [sourceOfIncome, setSourceOfIncome] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingCountries(true);
        setCountriesError(null);
        const res = await fetch("/xapi/countries", { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        const list: Country[] = Array.isArray(body) ? body : body?.data ?? [];
        if (mounted) setCountries(list.filter((c) => !!c?.name));
      } catch (e: any) {
        if (mounted) setCountriesError(e.message || "Failed to load countries");
      } finally {
        if (mounted) setLoadingCountries(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const countryOptions = useMemo(
    () => countries.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [countries]
  );

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    const fd = new FormData(e.currentTarget);

    try {
      await apiUboCreate(fd);
      nav("/profile/ubos");
    } catch (e: any) {
      setErr(e.message || "Failed to create UBO");
    } finally {
      setSaving(false);
    }
  }

  // 18 years ago for min-age helper (client-side only)
  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  )
    .toISOString()
    .slice(0, 10);

  return (
    <div className="max-w-3xl mx-auto pb-24">
      <div className="card-box">
        <h1 className="text-xl font-bold mb-4">Add New UBO</h1>

        <p className="text-sm text-gray-600 mb-4">
          You define the UBO’s identity here. The UBO will complete their own full
          KYC (ID + selfie + proof of address) later via a separate invite link. You
          no longer upload documents on their behalf.
        </p>

        {err && (
          <div className="alert-danger mb-4">
            <div className="font-medium">There was a problem</div>
            <div className="text-sm">{err}</div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Identity */}
          <div className="form-section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label mb-1 block">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  required
                  className="input block w-full"
                  autoComplete="given-name"
                  placeholder="e.g., Maria"
                />
              </div>
              <div>
                <label className="label mb-1 block">
                  Last name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  required
                  className="input block w-full"
                  autoComplete="family-name"
                  placeholder="e.g., Jensen"
                />
              </div>

              <div>
                <label className="label mb-1 block">
                  Birthdate <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="birthdate"
                  required
                  max={eighteenYearsAgo}
                  className="input block w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  UBO must be at least 18 years old.
                </p>
              </div>

              <div>
                <label className="label mb-1 block">
                  Nationality (country) <span className="text-red-500">*</span>
                </label>
                {loadingCountries ? (
                  <div className="skeleton h-10 rounded" />
                ) : countriesError ? (
                  <input
                    type="text"
                    name="nationality"
                    required
                    className="input block w-full"
                    placeholder="Type country name (e.g., Sweden)"
                  />
                ) : (
                  <select
                    name="nationality"
                    required
                    className="input block w-full"
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Select a country…
                    </option>
                    {countryOptions.map((c) => (
                      <option key={c.code || c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="label mb-1 block">
                  Ownership % <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="ownership_percent"
                  min={0}
                  max={100}
                  step="0.01"
                  required
                  className="input block w-full"
                  placeholder="e.g., 25"
                />
              </div>

              <div className="md:col-span-2">
                <label className="label mb-1 block">
                  Residential address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="residential_address"
                  required
                  className="input block w-full"
                  placeholder="Street, city, postal code, country"
                  autoComplete="street-address"
                />
              </div>

              <div>
                <label className="label mb-1 block">
                  Politically exposed person (PEP) <span className="text-red-500">*</span>
                </label>
                <select
                  name="is_pep"
                  required
                  value={isPep}
                  onChange={(e) => setIsPep(e.target.value)}
                  className="input block w-full"
                >
                  <option value="">Select...</option>
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              </div>

              <div>
                <label className="label mb-1 block">
                  Source of income <span className="text-red-500">*</span>
                </label>
                <select
                  name="source_of_income"
                  required
                  value={sourceOfIncome}
                  onChange={(e) => setSourceOfIncome(e.target.value)}
                  className="input block w-full"
                >
                  <option value="">Select...</option>
                  {incomeOptions.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {isPep === "1" && (
                <div className="md:col-span-2">
                  <label className="label mb-1 block">PEP details</label>
                  <textarea name="pep_details" required rows={3} maxLength={1000} className="input block w-full" />
                </div>
              )}

              {sourceOfIncome === "other" && (
                <div className="md:col-span-2">
                  <label className="label mb-1 block">Other source of income</label>
                  <input name="source_of_income_other" required maxLength={255} className="input block w-full" />
                </div>
              )}
            </div>
          </div>

          {/* Email for KYC invite */}
          <div className="form-section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label mb-1 block">
                  UBO Email (for KYC invite) <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="input block w-full"
                  placeholder="ubo@example.com"
                  autoComplete="email"
                />
                <p className="text-xs text-gray-500 mt-1">
                  We’ll use this email to send the UBO their secure KYC link.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-2 flex flex-col md:flex-row gap-3">
            <button className="btn md:w-auto w-full" disabled={saving}>
              {saving ? "Saving…" : "Save UBO"}
            </button>
            <button
              type="button"
              onClick={() => nav(-1)}
              className="btn btn-white md:w-auto w-full"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
